import React, { useEffect, useMemo, useState } from "react";
import {
  FileText, Folder, FolderOpen, ChevronRight, ChevronDown, Plus,
  Trash2, Download, ExternalLink, Loader2, Sparkles, X,
  Scale, Megaphone, Wallet, Headphones, BarChart3, Radio, Cpu,
  Rocket, Search,
} from "lucide-react";
import { toast } from "sonner";

import ConsoleShell from "@/components/ConsoleShell";
import api, { API_BASE } from "@/lib/api";

// ---------------------------------------------------------------------------
// Category → agent visual mapping (icon + color)
// ---------------------------------------------------------------------------

const CATEGORY_META = {
  "Facturation":       { icon: Wallet,     color: "#10B981", agent: "ULRICH" },
  "Juridique":         { icon: Scale,      color: "#EF4444", agent: "CASPER" },
  "Marketing":         { icon: Megaphone,  color: "#EC4899", agent: "CLARA"  },
  "Onboarding":        { icon: Headphones, color: "#8B5CF6", agent: "RHÉA"   },
  "Rapports":          { icon: BarChart3,  color: "#F97316", agent: "VISIO"  },
  "Scripts d'appel":   { icon: Radio,      color: "#A855F7", agent: "VOXY"   },
  "Ops":               { icon: Cpu,        color: "#06B6D4", agent: "OPÉRA"  },
  "Produit":           { icon: Rocket,     color: "#F59E0B", agent: "THIERRY"},
  "Divers":            { icon: FileText,   color: "#6B7280", agent: "GABY"   },
};

const catMeta = (name) => CATEGORY_META[name] || CATEGORY_META["Divers"];

// ---------------------------------------------------------------------------
// PDF helpers
// ---------------------------------------------------------------------------

const authFetch = (path) => {
  const token = localStorage.getItem("gabryx_token");
  return fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
};

const openPdf = (docId) =>
  authFetch(`/documents/${docId}/pdf`)
    .then((r) => r.blob())
    .then((b) => {
      const url = URL.createObjectURL(b);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    })
    .catch(() => toast.error("Erreur d'ouverture du PDF"));

const downloadPdf = (docId, filename) =>
  authFetch(`/documents/${docId}/pdf`)
    .then((r) => r.blob())
    .then((b) => {
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    })
    .catch(() => toast.error("Erreur de téléchargement"));

const fmtMoney = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR"); } catch { return iso; }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function Documents() {
  const [tree, setTree] = useState({});
  const [flat, setFlat] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState(null); // null = all
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState("");
  const [showNewMenu, setShowNewMenu] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);

  const refresh = () => {
    setLoading(true);
    api.get("/documents/all")
      .then((r) => {
        setTree(r.data.tree || {});
        setFlat(r.data.flat || []);
        // expand all top-level categories by default on first load
        if (Object.keys(expanded).length === 0) {
          const auto = {};
          for (const cat of Object.keys(r.data.tree || {})) auto[cat] = true;
          setExpanded(auto);
        }
      })
      .catch(() => toast.error("Erreur de chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const visible = useMemo(() => {
    let list = flat;
    if (selectedFolder) list = list.filter((d) => d.folder === selectedFolder);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((d) =>
        (d.title || "").toLowerCase().includes(q) ||
        (d.category || "").toLowerCase().includes(q) ||
        (d.agent_name || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [flat, selectedFolder, search]);

  const totalByCat = useMemo(() => {
    const c = {};
    for (const d of flat) c[d.category] = (c[d.category] || 0) + 1;
    return c;
  }, [flat]);

  return (
    <ConsoleShell title="Documents" breadcrumb={["GABRYX", "Documents"]}>
      <div className="flex h-[calc(100vh-8rem)] max-h-[calc(100vh-8rem)]" data-testid="documents-page">
        {/* Sidebar — folder tree */}
        <aside
          className="w-64 md:w-72 flex-shrink-0 border-r border-white/10 bg-white/[0.015] overflow-y-auto"
          data-testid="documents-sidebar"
        >
          <div className="p-4 border-b border-white/10">
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">
              Bibliothèque
            </div>
            <button
              onClick={() => setSelectedFolder(null)}
              data-testid="folder-all"
              className={`w-full text-left px-2.5 py-1.5 text-sm rounded transition ${
                selectedFolder === null
                  ? "bg-white/10 text-white"
                  : "text-zinc-400 hover:bg-white/5 hover:text-zinc-100"
              }`}
            >
              <span className="inline-flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />
                Tous les documents
                <span className="text-zinc-600 text-xs ml-auto">{flat.length}</span>
              </span>
            </button>
          </div>

          <div className="p-2">
            {Object.entries(tree).sort().map(([cat, years]) => {
              const meta = catMeta(cat);
              const Icon = meta.icon;
              const isOpen = expanded[cat];
              return (
                <div key={cat} className="mb-1">
                  <button
                    onClick={() => setExpanded({ ...expanded, [cat]: !isOpen })}
                    data-testid={`folder-toggle-${cat}`}
                    className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-white/5 transition flex items-center gap-1.5"
                  >
                    {isOpen ? <ChevronDown className="w-3 h-3 text-zinc-500" /> : <ChevronRight className="w-3 h-3 text-zinc-500" />}
                    {isOpen ? <FolderOpen className="w-3.5 h-3.5" style={{ color: meta.color }} /> : <Folder className="w-3.5 h-3.5" style={{ color: meta.color }} />}
                    <span className="font-medium text-zinc-200">{cat}</span>
                    <span className="text-[10px] text-zinc-600 ml-auto">{totalByCat[cat] || 0}</span>
                  </button>
                  {isOpen && (
                    <div className="ml-4 mt-0.5 border-l border-white/5 pl-1.5">
                      {Object.entries(years).sort((a, b) => b[0].localeCompare(a[0])).map(([year, months]) => (
                        <div key={year} className="mb-0.5">
                          <div className="px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] text-zinc-600">{year}</div>
                          {Object.entries(months).map(([month, docs]) => {
                            const folder = `${cat}/${year}/${month}`;
                            const active = selectedFolder === folder;
                            return (
                              <button
                                key={folder}
                                onClick={() => setSelectedFolder(folder)}
                                data-testid={`folder-${folder}`}
                                className={`w-full text-left px-2 py-1 text-xs rounded flex items-center gap-1.5 transition ${
                                  active
                                    ? "bg-white/10 text-white"
                                    : "text-zinc-400 hover:bg-white/5 hover:text-zinc-100"
                                }`}
                              >
                                <Folder className="w-3 h-3 opacity-60" />
                                <span>{month}</span>
                                <span className="text-[10px] text-zinc-600 ml-auto">{docs.length}</span>
                              </button>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {Object.keys(tree).length === 0 && !loading && (
              <div className="p-4 text-xs text-zinc-500 text-center">
                Aucun dossier pour l&apos;instant. Demandez à un agent de générer un document depuis le chat.
              </div>
            )}
          </div>
        </aside>

        {/* Main list */}
        <main className="flex-1 min-w-0 overflow-y-auto">
          <div className="p-5 lg:p-8">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">
                  {selectedFolder ? "Dossier" : "Tous les documents"}
                </div>
                <h1 className="font-display text-2xl font-semibold truncate" data-testid="folder-title">
                  {selectedFolder || "Bibliothèque complète"}
                </h1>
                <p className="text-xs text-zinc-500 mt-1">
                  {visible.length} document(s) · les agents rangent automatiquement leurs livrables ici.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-2.5" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher…"
                    data-testid="documents-search"
                    className="pl-8 pr-3 py-2 text-sm bg-black border border-white/10 focus:border-white/30 outline-none w-56"
                  />
                </div>
                <div className="relative">
                  <button
                    onClick={() => setShowNewMenu(!showNewMenu)}
                    data-testid="new-doc-btn"
                    className="inline-flex items-center gap-2 px-3.5 py-2 text-sm bg-white text-black hover:bg-zinc-200 transition"
                  >
                    <Plus className="w-4 h-4" />
                    Nouveau
                  </button>
                  {showNewMenu && (
                    <div
                      className="absolute right-0 top-full mt-1 w-72 bg-[#0a0a0a] border border-white/10 z-20 shadow-2xl"
                      data-testid="new-doc-menu"
                      onMouseLeave={() => setShowNewMenu(false)}
                    >
                      <button
                        onClick={() => { setShowInvoiceForm(true); setShowNewMenu(false); }}
                        data-testid="new-invoice-menu"
                        className="w-full text-left px-4 py-3 hover:bg-white/5 flex items-center gap-3 border-b border-white/5"
                      >
                        <Wallet className="w-4 h-4 text-emerald-400" />
                        <div>
                          <div className="text-sm text-zinc-100">Facture SaaS</div>
                          <div className="text-[11px] text-zinc-500">Ulrich — formulaire structuré</div>
                        </div>
                      </button>
                      <div className="px-4 py-3 text-xs text-zinc-500">
                        <div className="flex items-center gap-2 mb-1">
                          <Sparkles className="w-3 h-3 text-amber-400" />
                          <span className="text-zinc-300 font-medium">Autres documents</span>
                        </div>
                        Demandez directement à un agent dans le chat :
                        <ul className="mt-2 space-y-1 text-[11px]">
                          <li>• &quot;Casper, rédige les CGV SaaS&quot;</li>
                          <li>• &quot;Clara, écris l&apos;email de bienvenue&quot;</li>
                          <li>• &quot;Visio, rapport hebdo&quot;</li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* File list */}
            {loading ? (
              <div className="py-16 grid place-items-center text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : visible.length === 0 ? (
              <EmptyState hasSearch={!!search} hasFolder={!!selectedFolder} />
            ) : (
              <div className="border border-white/10" data-testid="documents-list">
                <div className="grid grid-cols-[24px_1fr_140px_140px_100px] gap-3 px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 border-b border-white/10 bg-white/[0.02]">
                  <div></div>
                  <div>Nom</div>
                  <div>Agent</div>
                  <div>Créé le</div>
                  <div className="text-right">Actions</div>
                </div>
                {visible.map((d) => {
                  const meta = catMeta(d.category);
                  const Icon = meta.icon;
                  return (
                    <div
                      key={d.id}
                      className="grid grid-cols-[24px_1fr_140px_140px_100px] gap-3 px-4 py-3 text-sm border-b border-white/5 hover:bg-white/[0.03] transition items-center"
                      data-testid={`doc-row-${d.id}`}
                    >
                      <Icon className="w-4 h-4" style={{ color: meta.color }} />
                      <div className="min-w-0">
                        <button
                          onClick={() => openPdf(d.id)}
                          data-testid={`doc-open-${d.id}`}
                          className="text-zinc-100 truncate hover:text-white text-left w-full"
                        >
                          {d.title}
                        </button>
                        <div className="text-[11px] text-zinc-600 truncate">
                          {d.category} · {d.folder}
                        </div>
                      </div>
                      <div className="text-xs">
                        <span
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] uppercase tracking-wider"
                          style={{ borderColor: `${meta.color}40`, color: meta.color }}
                        >
                          {d.agent_name || meta.agent}
                        </span>
                      </div>
                      <div className="text-zinc-400 text-xs">{fmtDateTime(d.created_at)}</div>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => openPdf(d.id)}
                          data-testid={`doc-view-${d.id}`}
                          className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10 transition"
                          title="Aperçu PDF"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => downloadPdf(d.id, `${d.title}.pdf`)}
                          data-testid={`doc-download-${d.id}`}
                          className="p-1.5 text-zinc-400 hover:text-white hover:bg-white/10 transition"
                          title="Télécharger"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={async () => {
                            if (!confirm(`Supprimer « ${d.title} » ?`)) return;
                            try {
                              await api.delete(`/documents/${d.id}`);
                              toast.success("Document supprimé");
                              refresh();
                            } catch {
                              toast.error("Suppression impossible");
                            }
                          }}
                          data-testid={`doc-delete-${d.id}`}
                          className="p-1.5 text-zinc-500 hover:text-red-400 transition"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>

      {showInvoiceForm && (
        <InvoiceForm
          onClose={() => setShowInvoiceForm(false)}
          onCreated={() => { setShowInvoiceForm(false); refresh(); }}
        />
      )}
    </ConsoleShell>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ hasSearch, hasFolder }) {
  if (hasSearch) {
    return (
      <div className="text-center py-16" data-testid="empty-search">
        <Search className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
        <div className="font-medium mb-2">Aucun résultat</div>
        <p className="text-sm text-zinc-500">Essayez avec d&apos;autres mots-clés ou retirez les filtres.</p>
      </div>
    );
  }
  if (hasFolder) {
    return (
      <div className="text-center py-16" data-testid="empty-folder">
        <Folder className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
        <div className="font-medium mb-2">Dossier vide</div>
        <p className="text-sm text-zinc-500">Aucun document rangé ici pour l&apos;instant.</p>
      </div>
    );
  }
  return (
    <div className="text-center py-16" data-testid="empty-all">
      <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
      <div className="font-medium mb-2">Aucun document</div>
      <p className="text-sm text-zinc-500 max-w-md mx-auto">
        Demandez à un agent dans le chat de générer un document. Exemples :
      </p>
      <div className="text-xs text-zinc-600 mt-3 space-y-1">
        <div>&quot;Ulrich, génère la facture d&apos;août pour Thierry, 29 €&quot;</div>
        <div>&quot;Casper, rédige mes CGV SaaS pour CHANTIX&quot;</div>
        <div>&quot;Clara, écris l&apos;email de bienvenue pour un nouveau pilote&quot;</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invoice creation form (Ulrich flow — kept from previous version)
// ---------------------------------------------------------------------------

function InvoiceForm({ onClose, onCreated }) {
  const [client, setClient] = useState({ name: "", company: "", address: "", email: "", siret: "" });
  const [items, setItems] = useState([{ description: "Abonnement CHANTIX Pilote — mensuel", quantity: 1, unit_price_ht: 29 }]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const total = items.reduce((acc, it) => acc + (Number(it.quantity) || 0) * (Number(it.unit_price_ht) || 0), 0);

  const updateItem = (i, key, val) => {
    setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  };

  const submit = async () => {
    if (!client.name.trim()) { toast.error("Nom du client requis"); return; }
    if (!items.some((i) => i.description.trim())) { toast.error("Au moins une ligne requise"); return; }
    setBusy(true);
    try {
      await api.post("/documents/invoices", {
        client,
        items: items
          .filter((i) => i.description.trim())
          .map((i) => ({
            description: i.description,
            quantity: Number(i.quantity) || 1,
            unit_price_ht: Number(i.unit_price_ht) || 0,
          })),
        notes,
      });
      toast.success("Facture créée");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur création");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Nouvelle facture SaaS" onClose={onClose} testid="invoice-form-modal">
      <div className="space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Client</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nom complet *" value={client.name} onChange={(v) => setClient({ ...client, name: v })} testid="invoice-client-name" />
            <Field label="Société" value={client.company} onChange={(v) => setClient({ ...client, company: v })} testid="invoice-client-company" />
            <Field label="Email" value={client.email} onChange={(v) => setClient({ ...client, email: v })} testid="invoice-client-email" />
            <Field label="SIRET" value={client.siret} onChange={(v) => setClient({ ...client, siret: v })} testid="invoice-client-siret" />
            <Field
              label="Adresse"
              value={client.address}
              onChange={(v) => setClient({ ...client, address: v })}
              testid="invoice-client-address"
              textarea
              className="col-span-2"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Lignes</div>
            <button
              onClick={() => setItems([...items, { description: "", quantity: 1, unit_price_ht: 0 }])}
              data-testid="invoice-add-line"
              className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" /> Ajouter une ligne
            </button>
          </div>
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-[1fr_80px_100px_30px] gap-2">
                <input
                  data-testid={`invoice-item-desc-${i}`}
                  value={it.description}
                  onChange={(e) => updateItem(i, "description", e.target.value)}
                  placeholder="Description"
                  className="bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
                />
                <input
                  type="number"
                  step="0.5"
                  data-testid={`invoice-item-qty-${i}`}
                  value={it.quantity}
                  onChange={(e) => updateItem(i, "quantity", e.target.value)}
                  className="bg-black border border-white/10 px-3 py-2 text-sm text-right focus:border-white/30 outline-none"
                />
                <input
                  type="number"
                  step="0.01"
                  data-testid={`invoice-item-pu-${i}`}
                  value={it.unit_price_ht}
                  onChange={(e) => updateItem(i, "unit_price_ht", e.target.value)}
                  className="bg-black border border-white/10 px-3 py-2 text-sm text-right focus:border-white/30 outline-none"
                />
                <button
                  onClick={() => setItems(items.filter((_, idx) => idx !== i))}
                  className="text-zinc-500 hover:text-red-400 transition"
                  disabled={items.length === 1}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-3 text-sm font-mono">
            <div className="text-zinc-500 mr-3">Total HT</div>
            <div className="font-bold" data-testid="invoice-total-preview">{fmtMoney(total)}</div>
          </div>
          <div className="text-right text-[10px] text-zinc-600 mt-1">
            TVA non applicable (auto-entreprise — art. 293 B CGI)
          </div>
        </div>

        <Field label="Notes (optionnel)" value={notes} onChange={setNotes} testid="invoice-notes" textarea />

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm border border-white/10 hover:bg-white/5 transition"
          >
            Annuler
          </button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="invoice-submit"
            className="px-4 py-2 text-sm bg-white text-black hover:bg-zinc-200 transition disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Créer la facture
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------

function Field({ label, value, onChange, testid, type = "text", textarea, placeholder, className = "" }) {
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">{label}</div>
      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          rows={3}
          className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none resize-none"
        />
      ) : (
        <input
          type={type}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={testid}
          className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-white/30 outline-none"
        />
      )}
    </div>
  );
}

function Modal({ title, onClose, children, testid }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid={testid}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <div className="font-display font-semibold">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-1" data-testid="modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
